"use client";
/**
 * One rAF loop while the line is open: reads the mic and remote analysers,
 * writes energy into refs (the ball reads them per frame, never React state)
 * and dispatches the you/bash speaking edges into the machine.
 */
import { useEffect } from "react";
import type { LineHandle } from "./liveClient";
import {
  liveBashRef,
  liveEnergyRef,
  liveTalkRef,
  useLiveStore,
} from "./liveStore";
import {
  bandFor,
  rmsOf,
  smooth,
  speakingEdge,
  type SpeakingState,
} from "./talkDriver";

/** Speech rms sits well under 1; lift it onto 0..1 so a normal voice fills the ball. */
const toEnergy = (rms: number) => Math.min(1, Math.sqrt(rms * 4));

export function useTalkDriver(
  handle: LineHandle | null,
  active: boolean
): void {
  useEffect(() => {
    if (!active || !handle) return;
    const dispatch = useLiveStore.getState().dispatch;
    const buf = new Float32Array(512);
    let raf = 0;
    let you: SpeakingState = { on: false, lastLoud: 0 };
    let bash: SpeakingState = { on: false, lastLoud: 0 };
    let youSm = 0;
    let bashSm = 0;
    // The band reads a smoothed rms on the rms scale (the thresholds live
    // there): a 10 ms window with no hysteresis would chatter the mouth.
    let bashRmsSm = 0;
    const loop = (now: number) => {
      let youRms = 0;
      let bashRms = 0;
      // Read the getters every frame: the remote track lands after the answer.
      const mic = handle.micAnalyser;
      const remote = handle.remoteAnalyser;
      if (mic) {
        mic.getFloatTimeDomainData(buf);
        youRms = rmsOf(buf);
      }
      if (remote) {
        remote.getFloatTimeDomainData(buf);
        bashRms = rmsOf(buf);
      }
      youSm = smooth(youSm, toEnergy(youRms));
      bashSm = smooth(bashSm, toEnergy(bashRms));
      liveEnergyRef.current = youSm;
      liveBashRef.current = bashSm;
      bashRmsSm = smooth(bashRmsSm, bashRms);
      liveTalkRef.current = bandFor(bashRmsSm);
      const y2 = speakingEdge(you, youRms, now);
      if (y2.on !== you.on) dispatch({ type: "you", on: y2.on });
      you = y2;
      const b2 = speakingEdge(bash, bashRms, now);
      if (b2.on !== bash.on) dispatch({ type: "bash", on: b2.on });
      bash = b2;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      // A loop that stops mid-speech (handle swap, active off, a close) must
      // not leave the machine believing a voice is on; the closure knows.
      if (you.on) dispatch({ type: "you", on: false });
      if (bash.on) dispatch({ type: "bash", on: false });
      liveEnergyRef.current = 0;
      liveBashRef.current = 0;
      liveTalkRef.current = 0;
    };
  }, [handle, active]);
}
